/**
 * ResearchSurface - the dedicated research shell.
 *
 * Deliberately NOT the generic chat shell. It composes the typed core:
 *
 *   useResearchSession  → the ResearchTurn[] transcript, history, sending
 *     └ useResearchRun  → the single hardened pubsub layer
 *   ResearchTranscript  → turns, lazy hydration, cursor pagination
 *     └ ResearchTurnView → ResearchResponseView (intent picks the renderer)
 *   ResearchComposer    → intent chip, attachments, settings entry
 *   SourceTree / SessionSettings → the per-index picker and overrides
 *
 * The old chat surface stays mounted at /research until this one is proven.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import {
  PiBriefcaseLight,
  PiNotePencilLight,
  PiCheckLight,
  PiCopySimpleLight,
  PiPencilSimpleLight,
  PiSidebarSimpleLight,
} from 'react-icons/pi';
import { Tooltip } from '@mui/material';
import Header from '../../shared/TailwindComponents/Header';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import BrandLoader from '../../shared/brand/BrandLoader';
import { RootState, useAppDispatch } from '../../../store';
import useAIPreferences from '../../../hooks/useAIPreferences';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { fetchAILanguages } from '../../../store/actions/DropdownActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import {
  ResearchLinkedMatter,
  ResearchSource,
  researchUploadAttachment,
} from '../../../store/actions/ChatAction';
import { EMPTY_CATALOGUE, loadSourceCatalogue } from '../sourceCatalogue';
import CitationDocumentViewer from '../../Chat/CitationDocumentViewer';
import ResearchCitationPanel from '../../Chat/ResearchCitationPanel';
import { ComposerIntent } from '../IntentSelector';
import SessionSettings, { SettingOption } from '../SessionSettings';
import SourceTree from '../SourceTree';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import MatterLink from '../MatterLink';
import useMatterLink from '../useMatterLink';
import useCitationSource, { canOpenSource } from '../useCitationSource';
import { isExternalUrl } from '../citationMarkers';
import useSessionSettings from '../useSessionSettings';
import {
  ComposerAttachment,
  attachmentBlobFrom,
  attachmentUuidFrom,
  chatDocuments,
  chatUploadedCitations,
  newAttachmentId,
  readyAttachmentUuids,
  sentAttachments,
} from '../attachments';
import { runSequentialUpload } from '../../../services/uploads/uploadBatching';
import { partitionOversized, oversizedMessage } from '../../../utils/uploadLimits';
import {
  shouldAdoptSources,
  soleJurisdiction,
  sourceKey,
  toSourceSelection,
} from '../sources';
import { CitationHighlight, CitationOpenRequest, ResearchCitation } from '../types';
import { isStreaming, orderTurns } from '../turns';
import { copyConversation } from '../conversationExport';
import { collectArtifacts } from '../artifacts';
import useResearchSession from './useResearchSession';
import ResearchHistorySidebar from './ResearchHistorySidebar';
import ArtifactPane from './ArtifactPane';
import ResearchHero from './ResearchHero';
import ResearchTranscript from './ResearchTranscript';
import { ClarifyLocator } from './ClarifyCard';
import { uploadLocator } from '../clarify';
import ResearchComposer from './ResearchComposer';
import SourceDocumentViewer from './SourceDocumentViewer';
import RevealingTitle from './RevealingTitle';
import { NEW_SESSION_TITLE, displayChatTitle } from './chatList';
import ChatDocuments from './ChatDocuments';
import Panel, { PanelDropZone } from './Panel';
import usePanelLayout from './usePanelLayout';
import usePanelDrag from '../../shared/surface/usePanelDrag';
import { DockSide, PanelId } from './layout';

const ResearchSurface: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const routeHistory = useHistory();
  const { hasSectionAccess, checkSectionGate } = useLicenseGate();
  const { chatGroupId } = useParams<{ chatGroupId?: string }>();
  /** The route's chat id on the previous render - see `routeChatCleared`. */
  const previousRouteChatRef = useRef<string | undefined>(chatGroupId);
  const { setBreadcrumbs } = useBreadcrumbs();

  const userInfo = useSelector((s: RootState) => (s as any).user?.userInfo);
  const session = useResearchSession(chatGroupId, userInfo?.UserID ?? userInfo?.userId ?? null);
  const settings = useSessionSettings(session.chatUuid || null);
  const matterLink = useMatterLink(session.chatUuid || null);
  /** Streamed bytes for a cited source, when the citation has a blob path. */
  const source = useCitationSource(session.chatUuid || null);
  /**
   * A matter chosen before the chat exists.
   *
   * There is nothing to PUT to yet, so the choice is held here and rides on
   * the create + first query instead - the backend's preferred flow, which
   * makes the relation with the chat row so even the first answer is grounded
   * in the matter. Once the chat exists this is dropped and `useMatterLink`
   * (server state) is the only truth.
   */
  const [pendingMatter, setPendingMatter] = useState<ResearchLinkedMatter | null>(null);
  /**
   * Output language chosen before the chat existed.
   *
   * Same shape as the pending matter: nothing to PUT to yet, so it is held
   * here, shown as the effective value, and written as the session override on
   * the first send - so a user who picks Bulgarian gets Bulgarian from the
   * very first answer instead of the English default.
   */
  /**
   * The saved preference, straight from the store - no fetch of our own. It is
   * hydrated by the platform bootstrap, so on mount it is already here and the
   * seeding effects below run on the first render rather than after a request.
   */
  const aiPreferences = useAIPreferences();
  const [pendingLanguage, setPendingLanguage] = useState<string>('');
  /**
   * True while `pendingLanguage` still holds the value seeded from the user's
   * AI preference rather than one they picked here - so the provenance line
   * says "Inherited from your settings" instead of claiming this chat
   * overrode anything.
   */
  const [pendingLanguageFromPref, setPendingLanguageFromPref] = useState(false);

  const [intent, setIntent] = useState<ComposerIntent>(null);
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const [selectedSources, setSelectedSources] = useState<ResearchSource[]>([]);
  const [availableSources, setAvailableSources] = useState<ResearchSource[]>([]);
  const [userDatasources, setUserDatasources] = useState<ResearchSource[]>([]);
  const [isLoadingSources, setIsLoadingSources] = useState(true);
  const [isRenaming, setIsRenaming] = useState(false);
  /** Which artifact the pane is showing, by turn id. */
  const [openArtifactId, setOpenArtifactId] = useState<string | null>(null);
  /** The artifact the pane auto-opened for, so it only ever does so once. */
  const autoOpenedRef = React.useRef<string | null>(null);
  const [expandedCountries, setExpandedCountries] = useState<Record<string, boolean>>({});

  const [citation, setCitation] = useState<{
    detail: ResearchCitation | null;
    highlight: CitationHighlight | null;
    url: string | null;
    name: string | null;
  } | null>(null);

  const aiLanguages = useSelector((s: RootState) => s.dropdown.dropdowns.aiLanguages);

  /* ── catalogue + languages ──────────────────────────────────── */

  // Fetched once per page session and shared - see `sourceCatalogue`. A
  // remount or a StrictMode double-invoke resolves from cache, no request.
  useEffect(() => {
    let cancelled = false;
    dispatch(fetchAILanguages());
    loadSourceCatalogue(dispatch)
      .catch(() => EMPTY_CATALOGUE)
      .then((catalogue) => {
        if (cancelled) return;
        setAvailableSources(catalogue.jurisdiction_sources);
        setUserDatasources(catalogue.user_datasources);
        setIsLoadingSources(false);
      });
    return () => { cancelled = true; };
  }, [dispatch]);

  const languageOptions = useMemo<SettingOption[]>(
    () => (aiLanguages || [])
      .map((lang: any) => ({
        value: String(lang.value ?? ''),
        label: String(lang.label ?? lang.value ?? ''),
      }))
      .filter((o: SettingOption) => o.value),
    [aiLanguages]
  );

  /* ── sources: the session's persisted selection is the truth ── */

  /**
   * Settings and the source selection are kept in step BY VALUE, not by
   * object identity.
   *
   * Every settings fetch/PUT returns a fresh `effective` object, so an
   * identity-keyed effect re-ran on each response, called `setSelectedSources`
   * with a new array, which re-triggered the sync effect, which PUT again —
   * a feedback loop that re-rendered the surface continuously. Comparing the
   * identifier sets breaks it: applying settings is a no-op when nothing
   * actually changed, so it can never reset or re-render the conversation.
   */
  const remoteSourceKeys = useMemo(
    () => (settings.effective.sources || []).map(sourceKey).sort().join('|'),
    [settings.effective.sources]
  );
  const localSourceKeys = useMemo(
    () => selectedSources.map(sourceKey).sort().join('|'),
    [selectedSources]
  );

  /**
   * True while `selectedSources` holds a selection made BEFORE this chat
   * existed - the start screen's. That, and only that, is what an empty
   * server answer must not wipe: it is the selection the chat is being
   * created with, and the read that would wipe it is the one that raced the
   * write. A selection belonging to a chat that HAS been read is ordinary
   * state, and the next chat's settings replace it as they always did.
   */
  const preChatSelectionRef = React.useRef(false);

  /**
   * Adopt what the server resolved. This is the ONLY writer of
   * `selectedSources` that is not a user action.
   */
  useEffect(() => {
    if (!settings.isReady) return;
    if (remoteSourceKeys === localSourceKeys) return;
    const remote = settings.effective.sources || [];
    /**
     * AN EMPTY SERVER ANSWER NEVER WIPES A SELECTION WE ARE HOLDING.
     *
     * The read fires the instant a chat exists, which on a chat created by
     * the first send is WHILE that send is still writing the scope. It
     * therefore answers with the pre-write emptiness - and adopting it
     * cleared the jurisdiction the user had ticked on the start screen the
     * moment the URL became /research/<uuid>. Empty means "keep what we
     * have" on the way in, exactly as it does on the way out (the settings
     * endpoint cannot clear a scope with `[]` either). The write that is in
     * flight bumps `session.scopeWrites`, which re-reads below and lands the
     * server's real answer.
     */
    if (preChatSelectionRef.current && !shouldAdoptSources(remote, selectedSources)) return;
    preChatSelectionRef.current = false;
    setSelectedSources(remote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.isReady, remoteSourceKeys]);

  /**
   * Re-read the settings once a send has persisted them.
   *
   * On a new chat the send is the only writer - the panel had no chat to PUT
   * to - so without this the panel would keep showing the answer from the
   * read that raced that write.
   */
  const scopeWrites = session.scopeWrites;
  useEffect(() => {
    if (!scopeWrites) return;
    settings.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeWrites]);

  /**
   * Before a chat exists there are no server-resolved settings to adopt, so an
   * unstarted session would show nothing ticked. Seed it from the user's AI
   * preference (`CountryOfJurisdiction || jurisdiction`) so the pref is the
   * visible default; the moment the user touches the tree this stops firing,
   * and once the chat is created the server resolution takes over.
   */
  const preferredJurisdiction = aiPreferences.jurisdiction;
  const seededPrefRef = React.useRef(false);
  useEffect(() => {
    if (session.chatUuid) return;
    if (seededPrefRef.current) return;
    if (!preferredJurisdiction || !availableSources.length) return;
    if (selectedSources.length) return;
    const preferred = availableSources.filter(
      (src) => (src.jurisdiction || '').toUpperCase() === preferredJurisdiction.toUpperCase()
    );
    if (!preferred.length) return;
    seededPrefRef.current = true;
    preChatSelectionRef.current = true;
    setSelectedSources(preferred);
  }, [session.chatUuid, preferredJurisdiction, availableSources, selectedSources.length]);

  /**
   * Same story for the answer language. Before a chat exists there is no
   * server-resolved `output_language`, so the control would read "Not set"
   * even for a user who has stated a preference. Seed it once, let any edit
   * win, and let the server's resolution take over once the chat is created.
   * An unset preference seeds nothing - unset is a valid answer.
   */
  const preferredLanguage = aiPreferences.language;
  const seededLanguageRef = React.useRef(false);
  useEffect(() => {
    if (session.chatUuid) return;
    if (seededLanguageRef.current) return;
    if (!preferredLanguage || pendingLanguage) return;
    seededLanguageRef.current = true;
    setPendingLanguage(preferredLanguage);
    setPendingLanguageFromPref(true);
  }, [session.chatUuid, preferredLanguage, pendingLanguage]);

  /**
   * Persist a USER edit, and only a user edit.
   *
   * This used to be an effect keyed on the local selection, which silently
   * wiped stored scope on every reopen: the adopt effect above only SCHEDULES
   * its state update, so the persist effect ran in the same commit still
   * holding the empty initial selection, saw it differ from the server's, and
   * PUT `sources: []`. The chat then had no sources for the next turn - which
   * is why a follow-up reached the engine with `selected_sources=[]` and it
   * searched every index. Writing from the change handler makes it impossible
   * for adoption to be mistaken for an edit.
   */
  const handleSourcesChange = useCallback((next: ResearchSource[]) => {
    setSelectedSources(next);
    if (!session.chatUuid) {
      // Picked on the start screen: it belongs to the chat the next send
      // creates, and must survive that chat's first settings read.
      preChatSelectionRef.current = true;
      return;
    }
    settings.patch({ sources: toSourceSelection(next) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.chatUuid, settings.patch]);

  /* ── clarify actions ────────────────────────────────────────── */

  /**
   * `attach_document` on a clarify card.
   *
   * NOT the composer's attach path. That one stages a pill to ride on the NEXT
   * query; this one supplies the document the OUTSTANDING question is about,
   * so it uploads immediately and hands back the locator the answer carries
   * (`upload:{uuid}`) - one gesture, not "attach, then remember to answer".
   *
   * §2.5 holds: the action is still an affordance. What becomes the answer is
   * the document it produced, which is a real locator the pipeline can
   * retrieve against (§4.3) rather than a name for one.
   */
  const handleClarifyAttach = useCallback(
    async (files: File[]): Promise<ClarifyLocator | null> => {
      const { accepted: picked, oversized } = partitionOversized(files);
      if (oversized.length) {
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'warning',
          message: t('Some files were too large to attach'),
          subMsg: oversizedMessage(oversized),
          duration: 8000,
        }));
      }
      const file = picked[0];
      if (!file) return null;

      const uuid = await session.ensureChat();
      if (!uuid) return null;
      const response: any = await dispatch(researchUploadAttachment(uuid, [file]));
      const attachmentUuid = attachmentUuidFrom(response);
      if (!attachmentUuid) return null;
      return { value: uploadLocator(attachmentUuid), label: file.name };
    },
    [dispatch, session.ensureChat, t]
  );

  /**
   * `link_matter` on a clarify card - the matter link lives in the settings
   * panel, so the action opens it rather than growing a second picker.
   */
  const handleClarifyLinkMatter = useCallback(
    () => layoutApi.setVisible('settings', true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /* ── attachments ────────────────────────────────────────────── */

  const handleAttachFiles = useCallback(async (all: File[]) => {
    if (!all.length) return;

    // A file over the cap cannot be batched around - one file is one request
    // no matter how it is grouped - so it is refused here with a reason
    // rather than reaching the edge and coming back as a protocol error.
    const { accepted: picked, oversized } = partitionOversized(all);
    if (oversized.length) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'warning',
        message: t('Some files were too large to attach'),
        subMsg: oversizedMessage(oversized),
        duration: 8000,
      }));
    }
    if (!picked.length) return;

    const staged: ComposerAttachment[] = picked.map((file) => ({
      id: newAttachmentId(),
      name: file.name,
      size: file.size,
      status: 'uploading',
    }));
    setAttachments((prev) => [...prev, ...staged]);

    const fail = (id: string, error: string) =>
      setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: 'failed', error } : a)));

    let uuid: string;
    try {
      uuid = await session.ensureChat(picked[0]?.name);
    } catch {
      staged.forEach((a) => fail(a.id, t('Could not start a chat for these files.')));
      return;
    }

    // The endpoint takes ONE file per request, so this is already batched at
    // the finest grain there is - but the requests went up all at once, which
    // put the same aggregate on the wire the single-body uploads did. They now
    // go in order, through the shared runner, one at a time.
    await runSequentialUpload(picked, {
      bounds: { maxFilesPerBatch: 1 },
      send: async (batch, ctx) => {
        const i = ctx.index;
        try {
          const response: any = await dispatch(researchUploadAttachment(uuid, batch));
          const attachmentUuid = attachmentUuidFrom(response);
          if (!attachmentUuid) throw new Error('missing uuid');
          const stored = attachmentBlobFrom(response);
          setAttachments((prev) =>
            prev.map((a) => (
              a.id === staged[i].id
                ? { ...a, status: 'ready', uuid: attachmentUuid, ...stored }
                : a
            ))
          );
          return response;
        } catch {
          // Handled per pill - one bad file must not stop the rest, so this
          // never propagates to the runner.
          fail(staged[i].id, t('Upload failed. Remove and try again.'));
          return { uploaded_count: 0 };
        }
      },
    });
  }, [dispatch, session, t]);

  /* ── sending ────────────────────────────────────────────────── */

  /** What the next send should write as this chat's jurisdiction override. */
  const pendingJurisdiction = session.chatUuid
    ? settings.overrides.jurisdiction
    : soleJurisdiction(selectedSources);

  const submit = useCallback(async (text: string, chosenIntent: ComposerIntent) => {
    const attachmentUuids = readyAttachmentUuids(attachments);
    const sent = await session.send(text, {
      intent: chosenIntent,
      ...(attachmentUuids.length ? { attachmentUuids } : {}),
      // The chips have to survive the send: the composer is cleared below,
      // and the message row may not echo the files back.
      ...(attachmentUuids.length ? { attachments: sentAttachments(attachments) } : {}),
      // Carried into the send so it is persisted before the query runs —
      // on a new chat the settings panel cannot have written it yet.
      sources: selectedSources,
      ...(!session.chatUuid && pendingMatter ? { matterUuid: pendingMatter.uuid } : {}),
      ...(!session.chatUuid && pendingLanguage ? { outputLanguage: pendingLanguage } : {}),
      /**
       * The jurisdiction override.
       *
       * On an existing chat it is whatever the user already set. On a chat
       * that does not exist yet there is no override to read, so it is
       * derived from what they ticked on the start screen - the chat is then
       * created WITH the jurisdiction they were looking at, instead of
       * inheriting one from their account that the selection may not even
       * serve. A selection spanning two countries derives nothing (see
       * `soleJurisdiction`).
       */
      ...(pendingJurisdiction ? { jurisdiction: pendingJurisdiction } : {}),
    });
    /**
     * Clear the composer ONLY when the question actually went out.
     *
     * A pill left behind after a successful send is silently re-attached to
     * the NEXT query - the user would be scoping a new question to a document
     * they thought they had finished with. The mirror risk is discarding
     * files on a send that never happened, so a blocked or failed submit
     * keeps everything exactly where it was.
     */
    if (!sent) return;
    if (attachmentUuids.length) setAttachments([]);
    // The chat now owns the matter; the local stand-in would only go stale.
    setPendingMatter(null);
    /**
     * `pendingLanguage` is deliberately NOT cleared here. It was just written
     * as this chat's override, but the settings read may still be in flight
     * (or may have answered before the write landed), and dropping it in that
     * window made the control fall back to "Not set" the moment the chat
     * appeared. It is superseded - not cleared - by the server's effective
     * value below, and by an explicit reset.
     */
  }, [attachments, session, selectedSources, pendingJurisdiction,
      pendingMatter, pendingLanguage]);

  /**
   * EDITING AN EXISTING QUESTION, in the main composer.
   *
   * The composer is where questions are written, so revising one belongs
   * there rather than in a second textarea on the turn. `token` forces the
   * seed back into the box even when re-editing the same question.
   *
   * A half-typed NEW question is STASHED, not discarded - clicking Edit must
   * never silently destroy work. It comes back on Cancel, and also after a
   * successful edit: the alternative is the user's unsent draft vanishing
   * because they revised something else in between.
   */
  /** Pushes text back into the composer - an empty string clears it. */
  const [restore, setRestore] = useState<{ text: string; token: number } | null>(null);

  const [edit, setEdit] = useState<
    { messageUuid: string; seed: string; token: number; stashed: string } | null
  >(null);
  /** The composer's current text, mirrored so a draft can be stashed. */
  const draftRef = useRef('');

  const startEdit = useCallback((messageUuid: string, seed: string) => {
    setEdit((prev) => ({
      messageUuid,
      seed,
      token: (prev?.token ?? 0) + 1,
      // Keep whatever was already stashed if the user hops between edits.
      stashed: prev ? prev.stashed : draftRef.current,
    }));
  }, []);

  /** Put the composer back exactly as the user left it. */
  const restoreDraft = useCallback((stashed: string) => {
    setEdit(null);
    setRestore({ text: stashed, token: Date.now() });
  }, []);

  const cancelEdit = useCallback(() => {
    restoreDraft(edit?.stashed || '');
  }, [edit, restoreDraft]);

  const handleSend = useCallback(
    (text: string) => {
      if (edit) {
        const { messageUuid, stashed } = edit;
        // A new VERSION of that turn - never a new exchange at the bottom.
        session.rerun(messageUuid, text);
        restoreDraft(stashed);
        return Promise.resolve();
      }
      return submit(text, intent);
    },
    [edit, restoreDraft, session, submit, intent]
  );

  /**
   * A hero suggestion sends immediately. Its intent is passed straight to the
   * send rather than set first and read back - a `setIntent` would not be
   * visible to this closure, so the example's shape would be lost. The chip is
   * updated too, so the composer reflects what was just sent.
   */
  const handleSuggestion = useCallback(
    (text: string, suggested?: ComposerIntent) => {
      const chosen = suggested ?? intent;
      setIntent(chosen);
      return submit(text, chosen);
    },
    [submit, intent]
  );

  /* ── route + title ──────────────────────────────────────────── */

  /**
   * BACK TO /research IS A FRESH SESSION.
   *
   * The "Research" crumb and the header's back caret both land on /research
   * with no chat id. The session hook clears its own transcript, title and run
   * state off that transition; the surface owns the rest of the screen —
   * attachments staged in the composer, a pending matter, an open citation or
   * artifact - and clears it here, so what is left is exactly a cold load.
   *
   * Computed during render, not in an effect: the replace below runs in the
   * same commit and must not put the id it just lost back in the URL.
   */
  const routeChatCleared = !chatGroupId && !!previousRouteChatRef.current;
  /**
   * A move from one chat straight to another - a pasted link, the back
   * button, anything that is not `selectChat`. The pending language belongs
   * to the chat it was picked for, so it must not follow the user into the
   * next one; that chat's own settings are the only truth there.
   */
  const routeChatSwitched =
    !!chatGroupId && !!previousRouteChatRef.current && chatGroupId !== previousRouteChatRef.current;
  useEffect(() => {
    previousRouteChatRef.current = chatGroupId;
  }, [chatGroupId]);
  useEffect(() => {
    if (!routeChatSwitched) return;
    preChatSelectionRef.current = false;
    setPendingLanguage('');
    setPendingLanguageFromPref(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeChatSwitched, chatGroupId]);
  useEffect(() => {
    if (routeChatCleared) clearWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeChatCleared]);

  // A chat created by sending the first message needs its uuid in the URL.
  useEffect(() => {
    if (routeChatCleared) return;
    if (session.chatUuid && !chatGroupId) {
      routeHistory.replace(`/research/${session.chatUuid}`);
    }
  }, [session.chatUuid, chatGroupId, routeChatCleared, routeHistory]);

  // Switching chats needs no effect here: `useResearchSession` takes the
  // route's uuid and opens it, and `open()` is idempotent per uuid so the
  // replace above cannot reload a transcript out from under a running query.

  /**
   * The chat's name in the header.
   *
   * "New Session" until the model writes one - never the backend's own
   * placeholder and never the raw question, which both read as a title the
   * user did not choose. The crumb takes a `node`, so it reveals the arriving
   * title through the same component the sidebar row uses: one motion, both
   * places, the same moment.
   */
  const chatCrumbTitle = displayChatTitle(session.title) || t(NEW_SESSION_TITLE);

  useEffect(() => {
    setBreadcrumbs(
      session.chatUuid
        ? [
            { name: 'Research', href: '/research', current: false },
            { name: chatCrumbTitle, href: `/research/${session.chatUuid}`, current: true },
          ]
        : [{ name: 'Research', href: '/research', current: true }]
    );
    return () => setBreadcrumbs([]);
  }, [chatCrumbTitle, session.chatUuid, setBreadcrumbs]);

  /* ── citations ──────────────────────────────────────────────── */

  /**
   * Open the ingested copy of a file the user attached.
   *
   * The upload endpoint is write-only, so the engine's citation of the
   * document is the only streamable handle - which is exactly what the
   * citation path already knows how to open.
   */
  /**
   * The run's retrieval records, in a ref.
   *
   * Read inside open handlers that must not re-create on every pubsub frame;
   * a ref keeps the handlers stable while still seeing the latest records.
   */
  const retrievalRef = useRef<Record<string, any> | null>(null);
  retrievalRef.current = session.run.retrievalData || null;

  const openAttachment = useCallback((cited: any) => {
    if (!canOpenSource(cited, retrievalRef.current)) return;
    source.open(cited, retrievalRef.current);
    setCitation({
      detail: cited,
      highlight: null,
      url: null,
      name: cited.title || cited.file_name || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.open]);

  const openCitationRequest = useCallback((request: CitationOpenRequest) => {
    const { citation: cited, target, highlight } = request;

    // The stored document wins: it is the actual source, with the cited
    // passage in it, rather than a snippet or an external landing page.
    // The run's retrieval records are consulted for a storage path when the
    // citation itself carries none - the common case for a corpus law.
    if (canOpenSource(cited, retrievalRef.current)) {
      source.open(cited, retrievalRef.current);
      setCitation({
        detail: cited,
        highlight,
        url: null,
        name: cited.title || cited.file_name || target,
      });
      return;
    }

    source.clear();
    // Not a streamable tenant document → if it has a navigable external URL
    // (web page, or a legislation source like legislation.gov.uk), the external
    // page IS the source. Open it regardless of snippet/highlight - the old
    // `!highlight && !cited.snippet` gate suppressed this for legislation/web
    // hits (which always carry a snippet), leaving them stuck on a dead
    // metadata panel. CitationDocumentViewer iframes the URL when framing is
    // allowed and otherwise opens it in a new tab.
    const external = isExternalUrl(cited.url) ? cited.url.trim() : (isExternalUrl(target) ? target : '');
    setCitation(
      external
        ? { detail: cited, highlight: null, url: external, name: null }
        : { detail: cited, highlight, url: null, name: cited.title || cited.file_name || target }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.open, source.clear]);

  const openCitationUrl = useCallback((url: string) => {
    source.clear();
    // Absolute http(s) only. This used to accept anything ending in .pdf/.docx
    // /.txt, which is exactly the shape of a tenant blob path - and handing
    // one to the viewer as a URL is how the app came to link at localhost.
    const isUrl = isExternalUrl(url);
    setCitation({ detail: null, highlight: null, url: isUrl ? url : null, name: isUrl ? null : url });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.clear]);

  /* ── Artifacts ──────────────────────────────────────────────
   * Derived from the turns already in memory. Reopening one from history
   * costs nothing: the envelope is whatever the lazy hydration produced.
   */
  const artifacts = useMemo(() => collectArtifacts(session.turns), [session.turns]);
  const openArtifact = artifacts.find((a) => a.turnId === openArtifactId) || null;

  /**
   * Auto-open once the intent says this run produces a document - which is
   * known from `research.intent`, before any content exists. For these
   * outputs the pane is where the progress belongs.
   */
  useEffect(() => {
    const streamingArtifact = artifacts.find((a) => a.isStreaming);
    if (!streamingArtifact) return;
    if (autoOpenedRef.current === streamingArtifact.turnId) return;
    autoOpenedRef.current = streamingArtifact.turnId;
    setOpenArtifactId(streamingArtifact.turnId);
  }, [artifacts]);

  /** Opening from a card: point the pane at the turn, and hydrate if needed. */
  const openArtifactById = useCallback((turnId: string) => {
    setOpenArtifactId(turnId);
    const target = artifacts.find((a) => a.turnId === turnId);
    // A history turn whose blob has not landed yet - the same lazy hydration
    // the transcript uses fills the pane in. Nothing is re-run.
    if (target && !target.response && target.messageUuid) {
      session.hydrate(target.messageUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifacts, session.hydrate]);

  const running = session.turns.some(isStreaming);

  /**
   * Copy the whole session - questions, answers, and a compact source
   * register. Never the source documents themselves; see
   * `conversationToMarkdown`.
   */
  const [copied, setCopied] = useState(false);
  const copyChat = useCallback(async () => {
    const ok = await copyConversation(orderTurns(session.turns), { title: session.title });
    if (!ok) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Could not copy the conversation'),
        showModal: true,
        duration: 3000,
      }));
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [dispatch, session.turns, session.title, t]);

  /* Every file uploaded in this chat, and every citation that could be an
     ingested copy of one - see `ChatDocuments`. */
  const documents = useMemo(() => chatDocuments(session.turns as any), [session.turns]);
  const documentCitations = useMemo(
    () => chatUploadedCitations(session.turns as any),
    [session.turns]
  );

  /* ── Panel layout ───────────────────────────────────────────
   * Floating glass surfaces over a full-bleed chat canvas: docked left or
   * right, stacked vertically, resizable and re-dockable. The transcript is
   * not one of them - it is the canvas underneath.
   */
  const layoutApi = usePanelLayout();
  /** Pinning the document panel removes its close control. */
  const [artifactPinned, setArtifactPinned] = useState(false);

  /**
   * The drag, from the shared implementation.
   *
   * This used to be written out here - the hit-test, the drop, and the
   * window-level listener net that stops a drag surviving the gesture that
   * ended it. Review and Tables then needed the same thing and only one of
   * them got it. See `usePanelDrag`.
   */
  const drag = usePanelDrag<PanelId>(
    useCallback(
      (id, target) => layoutApi.move(id, target.dock, target.index),
      [layoutApi]
    ),
    !layoutApi.singleColumn
  );

  const documentContent = openArtifact
    ? 'artifact'
    : source.isLoading || source.kind || (citation && (citation.url || citation.name))
    ? 'citation'
    : null;

  // The document slot opens and closes with what it holds.
  useEffect(() => {
    layoutApi.setVisible('document', !!documentContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!documentContent]);

  const closeDocument = useCallback(() => {
    setOpenArtifactId(null);
    setCitation(null);
    source.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.clear]);


  /**
   * Switching chats goes through the ROUTE, not a direct session call: the
   * route is the single source of truth for which chat is open, so the
   * back button and a pasted link behave the same as a sidebar click.
   */
  const selectChat = useCallback((uuid: string) => {
    if (!uuid || uuid === session.chatUuid) return;
    // Whatever is ticked belongs to the chat being left, not the one being
    // opened - that chat's own settings decide from here.
    preChatSelectionRef.current = false;
    setAttachments([]);
    setPendingMatter(null);
    setPendingLanguage('');
    setCitation(null);
    setOpenArtifactId(null);
    autoOpenedRef.current = null;
    routeHistory.push(`/research/${uuid}`);
  }, [routeHistory, session.chatUuid]);

  /**
   * Everything on this screen that is ABOUT the open chat.
   *
   * Shared by "New chat" and by a navigation back to /research, so the two
   * land on the same blank surface rather than each clearing its own subset.
   */
  const clearWorkspace = useCallback(() => {
    session.reset();
    // Back on the start screen: whatever stays ticked is once again a
    // selection waiting for a chat to be created with it.
    preChatSelectionRef.current = true;
    setAttachments([]);
    setPendingMatter(null);
    setPendingLanguage('');
    setCitation(null);
    setOpenArtifactId(null);
    autoOpenedRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newChat = useCallback(() => {
    clearWorkspace();
    routeHistory.push('/research');
  }, [clearWorkspace, routeHistory]);

  /* ── Canvas + floating docks ─────────────────────────────────
   * The transcript is the canvas: full-bleed, always present, never tiled.
   * Panels float over it, docked left or right and stacked vertically.
   */

  /** A panel's contents. The frame - glass, width, resize, drag - is Panel's. */
  const panelBody = (id: PanelId) => {
    switch (id) {
      case 'history':
        return (
          <ResearchHistorySidebar
            activeChatUuid={session.chatUuid}
            onSelect={selectChat}
            onNewChat={newChat}
          />
        );

      case 'document':
        return openArtifact ? (
          <ArtifactPane
            artifact={openArtifact}
            artifacts={artifacts}
            onSelect={openArtifactById}
            onPinnedChange={setArtifactPinned}
            citation={
              citation && citation.name
                ? { detail: citation.detail, highlight: citation.highlight, name: citation.name }
                : null
            }
            onOpenCitation={openCitationUrl}
            onCloseCitation={() => setCitation(null)}
            retrievalData={session.run.retrievalData || undefined}
          />
        ) : source.isLoading ? (
          <div className="h-full flex items-center justify-center" data-testid="source-loading">
            <BrandLoader size="sm" />
          </div>
        ) : source.kind ? (
          // A streamed source: an HTML document goes to the sandboxed frame,
          // text keeps the markdown path.
          <SourceDocumentViewer
            title={citation?.detail?.title || citation?.name || undefined}
            kind={source.kind}
            url={source.url}
            text={source.text}
            fileName={citation?.detail?.file_name || citation?.name || undefined}
            contentType={source.contentType}
            citation={citation?.detail}
            citationNumber={citation?.detail?.number}
            locators={source.locators}
            onClose={closeDocument}
          />
        ) : citation?.url ? (
          <CitationDocumentViewer url={citation.url} onClose={closeDocument} />
        ) : citation ? (
          <div className="h-full flex flex-col min-h-0">
            {source.failed && (
              <p className="flex-shrink-0 px-3 py-2 text-[11px] text-fg-muted
                            border-b border-hairline-soft"
                 data-testid="source-failed">
                {t('Could not load this source. The excerpt below is what was cited.')}
              </p>
            )}
            <div className="flex-1 min-h-0">
              <ResearchCitationPanel
                citationName={citation.name || ''}
                citation={citation.detail}
                highlight={citation.highlight}
                retrievalData={session.run.retrievalData || undefined}
                onClose={closeDocument}
              />
            </div>
          </div>
        ) : null;

      case 'settings':
      default:
        return (
          <div className="h-full overflow-y-auto p-3">
            <div className="mb-4 pb-4 border-b border-hairline-soft">
              <MatterLink
                matter={matterLink.matter || pendingMatter}
                isLoading={matterLink.isLoading}
                isSaving={matterLink.isSaving}
                onLink={(uuid, picked) => {
                  if (session.chatUuid) matterLink.link(uuid);
                  else setPendingMatter(picked ?? { uuid });
                }}
                onUnlink={() => {
                  if (session.chatUuid) matterLink.unlink();
                  setPendingMatter(null);
                }}
              />
            </div>

            <div className="mb-4 pb-4 border-b border-hairline-soft">
              <SessionSettings
                outputLanguage={settings.effective.output_language || pendingLanguage}
                outputLanguageSource={
                  // The server's provenance wins as soon as it has one; the
                  // pending value stands in for it before and across the
                  // create, so the line never reads "Not set" under a
                  // language the user just picked.
                  settings.effective.output_language
                    ? settings.effective.output_language_source
                    : pendingLanguage
                      ? (pendingLanguageFromPref ? 'ai_settings' : 'session')
                      : settings.effective.output_language_source
                }
                languageOptions={languageOptions}
                onOutputLanguageChange={(language) => {
                  // With a chat, write it through; without one, hold it for
                  // the first send.
                  if (session.chatUuid) settings.setOutputLanguage(language);
                  else { setPendingLanguage(language); setPendingLanguageFromPref(false); }
                }}
                onOutputLanguageReset={() => {
                  if (session.chatUuid) settings.clearOutputLanguage();
                  setPendingLanguage('');
                  setPendingLanguageFromPref(false);
                }}
                disabled={!session.chatUuid}
                isLoading={aiPreferences.isHydrating || settings.isLoading || !aiLanguages}
                isSaving={settings.isSaving}
              />
            </div>

            {isLoadingSources || aiPreferences.isHydrating ? (
              <Skeleton variant="list" rows={7} />
            ) : (
              <>
              {/* Unset scope is valid, not an error - say what it means. */}
              {selectedSources.length === 0 && (
                <p className="text-[11px] text-fg-muted mb-2">
                  {t('No jurisdiction selected — all jurisdictions are searched.')}
                </p>
              )}
              <SourceTree
                jurisdictionSources={availableSources}
                userDatasources={userDatasources}
                selected={selectedSources}
                onChange={handleSourcesChange}
                expanded={expandedCountries}
                onToggleExpanded={(code) =>
                  setExpandedCountries((prev) => ({ ...prev, [code]: prev[code] === false }))
                }
                dataSourcesLocked={!hasSectionAccess('data_sources')}
                onUpgradeDataSources={() => checkSectionGate('data_sources')}
              />
              </>
            )}
          </div>
        );
    }
  };

  const panelTitle = (id: PanelId): string => {
    if (id === 'history') return t('History');
    if (id === 'settings') return t('Sources & settings');
    return openArtifact ? openArtifact.title : t('Source');
  };

  const closePanel = (id: PanelId) => {
    if (id === 'document') { closeDocument(); return; }
    layoutApi.setVisible(id, false);
  };

  const renderFloat = (slot: { id: PanelId; dock: DockSide; width: number }) => (
    <Panel
      key={slot.id}
      id={slot.id}
      dock={slot.dock}
      // The DOCK's width, not the panel's own: a column of three at three
      // different widths reads as broken.
      width={layoutApi.width(slot.dock)}
      title={panelTitle(slot.id)}
      actions={slot.id === 'history' ? (
        <Tooltip title={t('New research')}>
          <button
            type="button"
            onClick={newChat}
            data-testid="history-new-chat"
            aria-label={t('New research')}
            className="p-1 rounded-md text-fg-muted hover:text-fg-strong
                       transition-colors duration-200 ease-slide"
          >
            <PiNotePencilLight className="w-4 h-4" />
          </button>
        </Tooltip>
      ) : undefined}
      zIndex={layoutApi.zIndexOf(slot.id)}
      draggable={!layoutApi.singleColumn}
      onDragStart={() => drag.onDragStart(slot.id)}
      onDragMove={drag.onDragMove}
      onDragEnd={drag.onDragEnd}
      onFocus={() => layoutApi.focus(slot.id)}
      onResize={(next) => layoutApi.resize(slot.id, next)}
      // Pinning the document keeps it open: the control goes rather than
      // sitting there disabled.
      onClose={slot.id === 'document' && artifactPinned ? undefined : () => closePanel(slot.id)}
    >
      {panelBody(slot.id)}
    </Panel>
  );

  /** One dock: a column of floating panels, inset from the viewport edge. */
  const renderDock = (side: DockSide) => {
    const panels = layoutApi.dock(side);
    const dragging = drag.active;
    if (panels.length === 0 && !dragging) return null;

    return (
      <div
        data-testid={`dock-${side}`}
        className={clsx(
          // Floating over the canvas: the dock itself catches no pointer
          // events, so the chat behind it stays clickable between panels.
          'absolute top-3 bottom-3 z-30 flex flex-col gap-3 pointer-events-none',
          side === 'left' ? 'start-3' : 'end-3',
          // An empty dock is invisible until a drag needs somewhere to land.
          dragging && panels.length === 0 && 'w-64',
          layoutApi.scrolls(side) && 'overflow-y-auto'
        )}
      >
        <PanelDropZone
          dock={side}
          index={0}
          active={dragging}
          hovered={drag.isHovered(side, 0)}
          label={panels.length === 0 ? t('Drop here') : undefined}
        />
        {panels.map((slot, i) => (
          <React.Fragment key={slot.id}>
            {renderFloat(slot)}
            <PanelDropZone
              dock={side}
              index={i + 1}
              active={dragging}
              hovered={drag.isHovered(side, i + 1)}
            />
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-bg"
         data-testid="research-surface">
      {/* ── App header ───────────────────────────────────────────
          A sibling ABOVE the stage, not a layer inside it. The docks are
          absolutely positioned within the stage below, so no floating panel
          can reach the breadcrumb or the toggles. */}
      <div className="glass-bar flex-shrink-0 border-b border-hairline-soft z-50"
           data-testid="research-header">
          <Header
            /* Back out of the chat, not back through the URLs this surface
               rewrote on its way here. Same landing as the Research crumb. */
            onBack={session.chatUuid ? newChat : undefined}
            breadcrumbs={
              session.chatUuid
                ? [
                    { name: 'Research', href: '/research', current: false },
                    {
                      name: chatCrumbTitle,
                      href: `/research/${session.chatUuid}`,
                      current: true,
                      node: (
                        <RevealingTitle
                          as="span"
                          text={chatCrumbTitle}
                          className="max-w-[18rem] truncate align-bottom"
                        />
                      ),
                    },
                  ]
                : [{ name: 'Research', href: '/research', current: true }]
            }
            additionalRightElements={
              <>
                {session.chatUuid && (
                  isRenaming ? (
                    <input
                      autoFocus
                      defaultValue={session.title}
                      data-testid="chat-rename-input"
                      aria-label={t('Rename chat')}
                      onBlur={(e) => { session.rename(e.target.value); setIsRenaming(false); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          session.rename((e.target as HTMLInputElement).value);
                          setIsRenaming(false);
                        }
                        if (e.key === 'Escape') setIsRenaming(false);
                      }}
                      className="px-2 py-1 me-1 rounded-md text-xs bg-surface-sunken
                                 text-fg-body border border-hairline-soft
                                 focus:outline-none focus:border-champagne/50"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsRenaming(true)}
                      data-testid="chat-rename"
                      aria-label={t('Rename chat')}
                      title={t('Rename chat')}
                      className="p-2 rounded-md text-fg-muted hover:text-fg-strong
                                 hover:bg-surface-sunken/70 transition-colors duration-200 ease-slide"
                    >
                      <PiPencilSimpleLight size={16} />
                    </button>
                  )
                )}
                {(matterLink.matter || pendingMatter) && (
                  <button
                    type="button"
                    onClick={() => layoutApi.setVisible('settings', true)}
                    data-testid="matter-chip"
                    title={t('Linked matter — open settings to change')}
                    className="inline-flex items-center gap-1.5 px-2 py-1 me-1 rounded-full
                               text-[11px] font-medium max-w-[14rem]
                               bg-champagne/15 border border-champagne/40 text-fg-strong
                               hover:border-champagne transition-colors duration-200 ease-slide"
                  >
                    <PiBriefcaseLight className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
                    <span className="truncate">
                      {(matterLink.matter || pendingMatter)?.title || t('Linked matter')}
                    </span>
                  </button>
                )}
                {session.turns.length > 0 && (
                  <button
                    type="button"
                    onClick={copyChat}
                    data-testid="copy-conversation"
                    aria-label={t('Copy conversation')}
                    title={t('Copy conversation')}
                    className="p-2 rounded-md text-fg-muted hover:text-fg-strong
                               hover:bg-surface-sunken/70 transition-colors duration-200 ease-slide"
                  >
                    {copied
                      ? <PiCheckLight size={16} className="text-champagne" />
                      : <PiCopySimpleLight size={16} />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => layoutApi.toggle('history')}
                  aria-label={t('Toggle history')}
                  data-testid="toggle-history"
                  className="p-2 rounded-md text-fg-muted hover:text-fg-strong
                             hover:bg-surface-sunken/70 transition-colors duration-200 ease-slide"
                >
                  <PiSidebarSimpleLight size={18} />
                </button>
              </>
            }
          />
      </div>

      {/* ── The stage: canvas underneath, floating docks over it ── */}
      <div className="relative flex-1 min-h-0" data-testid="research-stage">
        {/* The canvas runs edge to edge; only the READING COLUMN is inset, so
            nothing legible ends up behind a floating panel. */}
        <div
          className="absolute inset-0 flex flex-col transition-[padding] duration-300 ease-slide"
          style={{
            paddingLeft: layoutApi.inset('left'),
            paddingRight: layoutApi.inset('right'),
          }}
          data-testid="canvas-column"
        >
          {session.turns.length === 0 && !session.isLoadingHistory ? (
            <ResearchHero onSelect={handleSuggestion} disabled={running} />
          ) : (
            <ResearchTranscript
              turns={session.turns}
              isLoadingHistory={session.isLoadingHistory}
              isLoadingOlder={session.isLoadingOlder}
              hasMoreHistory={session.hasMoreHistory}
              onLoadOlder={session.loadOlder}
              onHydrate={session.hydrate}
              onOpenCitation={openCitationUrl}
              onOpenCitationRequest={openCitationRequest}
              artifacts={artifacts}
              openArtifactTurnId={openArtifactId}
              onOpenArtifact={openArtifactById}
              onFollowUp={handleSend}
              onOpenAttachment={openAttachment}
              onRerun={session.rerun}
              onEdit={startEdit}
              onSelectVersion={session.selectVersion}
              onRate={session.rate}
              onRefine={session.refine}
              onAnswerClarification={session.answerClarification}
              onAbandonClarification={session.abandonClarification}
              onClarifyAttach={handleClarifyAttach}
              onClarifyLinkMatter={handleClarifyLinkMatter}
            />
          )}

          <ResearchComposer
            onSend={handleSend}
            onStop={session.stop}
            isRunning={running}
            intent={intent}
            onIntentChange={setIntent}
            attachments={attachments}
            onAttachFiles={handleAttachFiles}
            onRemoveAttachment={(id) => setAttachments((prev) => prev.filter((a) => a.id !== id))}
            edit={edit ? { messageUuid: edit.messageUuid, seed: edit.seed, token: edit.token } : null}
            onCancelEdit={cancelEdit}
            restore={restore}
            onDraftChange={(text) => { draftRef.current = text; }}
            onOpenSettings={() => layoutApi.toggle('settings')}
            sourceCount={selectedSources.length}
            documents={(
              <ChatDocuments
                documents={documents}
                citations={documentCitations}
                onOpen={openAttachment}
              />
            )}
          />

          <p className="text-xs text-fg-muted self-center pb-2">
            {t('Built to support, not supplant, professional judgment. Confirm critical findings.')}
          </p>
        </div>

        {/* ── Floating docks, over the canvas ───────────────────── */}
        {renderDock('left')}
        {renderDock('right')}
      </div>

      {/* ── Narrow viewport: one sheet, with a scrim ─────────────── */}
      {layoutApi.singleColumn && layoutApi.sheet && (
        <>
          <button
            type="button"
            aria-label={t('Close panel')}
            data-testid="sheet-scrim"
            onClick={() => closePanel(layoutApi.sheet as PanelId)}
            className="absolute inset-0 z-30 bg-ink/20 backdrop-blur-[2px]"
          />
          <div className="absolute inset-y-0 end-0 z-40 p-2" data-testid="panel-sheet">
            <Panel
              id={layoutApi.sheet}
              dock="right"
              width={0}
              sheet
              zIndex={40}
              title={panelTitle(layoutApi.sheet)}
              onClose={() => closePanel(layoutApi.sheet as PanelId)}
            >
              {panelBody(layoutApi.sheet)}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
};

export default ResearchSurface;
